import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { addSchemaValue } from 'ravenjs/utils/schema';
import { CONTENT_TYPES, CATEGORY_NAMES } from 'constants/content';
import MESSAGES from 'constants/messages';
import { NEWSLETTERS } from 'constants/subscriptions';
import USERS from 'constants/users';
import { isValidPhoneNumber } from './thrservice';
import { isLawUpdatesSubscriptionFeatureEnabled } from './company';

/**
 * Fetch and format law updates categories from Contentful.
 * Does NOT include any feature-flag checks internally.
 * The caller is responsible for gating the call behind
 * isLawUpdatesSubscriptionFeatureEnabled() or similar flags.
 *
 * @param {Function} fetchContentfulCategories - API function to fetch categories
 * @return {Promise<Array>} Formatted categories array with parent/subcategory structure
 */
export const fetchAndFormatLawUpdatesCategories = async fetchContentfulCategories => {
    if (!fetchContentfulCategories) {
        return [];
    }
    try {
        const contentfulCategoriesResponse = await fetchContentfulCategories({
            contentType: CONTENT_TYPES.LAW,
        });
        const categories = get(contentfulCategoriesResponse, 'data.categories', []);
        const filteredCategories = categories.filter(
            category => category.name !== CATEGORY_NAMES.OTHER
        );
        return filteredCategories.flatMap(category => {
            const subcategories =
                category.subcategory && category.subcategory.length > 0
                    ? category.subcategory.map(sub => ({
                          id: sub.id,
                          label: sub.name,
                          value: sub.id,
                          parentId: category.id,
                      }))
                    : [];
            const parentCategory = {
                id: category.id,
                label: category.name,
                value: category.id,
                color: category.bulletColor,
                disabled: true,
                ...(subcategories.length > 0 && { subcategories }),
            };
            return [parentCategory, ...subcategories];
        });
    } catch {
        return [];
    }
};

/**
 * Expand the 'jurisdictionAll' sentinel in a states array to the full list
 * of real state values. Used when constructing the outgoing API payload so
 * the backend never receives the sentinel value.
 *
 * @param {Array} states - The states array from formData (may contain 'jurisdictionAll')
 * @param {Array} allStateValues - All available real state values (from schema)
 * @return {Array} States array with 'jurisdictionAll' replaced by all real values
 */
export const expandJurisdictionAll = (states, allStateValues) => {
    if (!Array.isArray(states)) return states;
    if (states.includes('jurisdictionAll')) {
        return allStateValues || [];
    }
    return states;
};

/**
 * Return a copy of profile formData whose lawAlerts/lawUpdates `states` are
 * expanded from the 'jurisdictionAll' sentinel and sorted, so that the UI's
 * collapsed/reordered representation compares equal to the API baseline.
 *
 * @param {Object} formData - Profile form data
 * @param {Object} stateValues
 * @param {Array} stateValues.alertStateValues - All lawAlerts state values (from schema)
 * @param {Array} stateValues.updateStateValues - All lawUpdates state values (from schema)
 * @return {Object} Normalized copy (input is not mutated)
 */
export const normalizeSubscriptionStates = (formData, { alertStateValues, updateStateValues }) => {
    const userSubscriptions = get(formData, 'subscriptions.userSubscriptions');
    if (!userSubscriptions) return formData;

    const normalizeStates = (subscription, allStateValues) => {
        if (!subscription || !Array.isArray(subscription.states)) return subscription;
        return {
            ...subscription,
            states: [...expandJurisdictionAll(subscription.states, allStateValues)].sort((a, b) =>
                String(a).localeCompare(String(b))
            ),
        };
    };

    return {
        ...formData,
        subscriptions: {
            ...formData.subscriptions,
            userSubscriptions: {
                ...userSubscriptions,
                lawAlerts: normalizeStates(userSubscriptions.lawAlerts, alertStateValues),
                lawUpdates: normalizeStates(userSubscriptions.lawUpdates, updateStateValues),
            },
        },
    };
};

/**
 * Normalize user subscriptions data by ensuring all expected fields exist.
 * Handles data shape normalization — fills in defaults for missing fields.
 * Does NOT include any flag-gating internally. When a flag is off, the field
 * simply won't be present and this function fills in the default.
 *
 * @param {Object} userSubscriptions - Raw user subscriptions object
 * @return {Object} Normalized user subscriptions object
 */
export const filterUserSubscriptionsData = userSubscriptions => {
    delete userSubscriptions.preferenceId;
    if (!(NEWSLETTERS.NEWS_LETTERS in userSubscriptions)) userSubscriptions.newsletters = [];
    if (!(NEWSLETTERS.INSIGHTS in userSubscriptions)) userSubscriptions.insights = false;
    if (!(NEWSLETTERS.LAW_ALERT in userSubscriptions))
        userSubscriptions.lawAlerts = { isSubscribed: false, states: [] };
    if (isLawUpdatesSubscriptionFeatureEnabled() && !('lawUpdates' in userSubscriptions))
        userSubscriptions.lawUpdates = { isSubscribed: false, states: [], categories: [] };
    return userSubscriptions;
};

/**
 * Check if any subscription data exists / has been updated.
 * Checks lawUpdates in addition to insights, newsletters, and lawAlerts.
 * When lawUpdates flag is off, the field won't be present so the check
 * naturally returns false for it — no flag checks inside this function.
 *
 * @param {Object} userSubscriptions - User subscriptions object
 * @return {boolean} True if any subscription data exists
 */
export const isUserSubscriptionUpdated = (userSubscriptions = {}) => {
    const { insights, newsletters = [], lawAlerts = {}, lawUpdates = {} } = userSubscriptions;
    const isSubscribedToNewsletters = !isEmpty(newsletters);
    const isSubscribedToLawAlerts = get(lawAlerts, 'isSubscribed', false);
    const isSubscribedToLawUpdates = get(lawUpdates, 'isSubscribed', false);
    return (
        insights || isSubscribedToNewsletters || isSubscribedToLawAlerts || isSubscribedToLawUpdates
    );
};

export const SECTION_BOX_PROPS = {
    padding: '1.5rem 0',
    margin: '0 0 1.25rem 0',
    elevation: 99,
    boxShadow: '0px 2px 8px -2px rgba(40, 40, 40, 0.16)',
    borderRadius: '8px',
    borderWidth: '0',
    backgroundColor: '#ffffff',
};

export const SECTION_TITLE_PROPS = {
    fontFamily: "'ABC Favorit Extended', Favorit, sans-serif",
    fontWeight: '500',
    fontSize: '18px',
    color: '#58595B',
};

export const SUBSCRIPTION_BOX_PROPS = {
    padding: '0',
    margin: '0 0 1.25rem 0',
    elevation: 0,
    borderWidth: '0',
    backgroundColor: 'transparent',
};

/**
 * Apply section card styling to form schema sections when the law updates
 * subscription feature is enabled. Adds boxProps, titleInsideFormGroup, and
 * titleProps to each section, and transparent container styling to subscriptions.
 *
 * @param {Object} schema - The form schema object to mutate
 * @param {string[]} sections - Schema paths for sections to style (e.g. 'properties.userInfo')
 * @param {string} [subscriptionPath] - Schema path to subscriptions section (e.g. 'properties.subscriptions')
 */
export const applySectionStyling = (
    schema,
    sections,
    subscriptionPath = 'properties.subscriptions'
) => {
    sections.forEach(section => {
        if (get(schema, `${section}.type`)) {
            addSchemaValue(schema, `${section}.boxProps`, SECTION_BOX_PROPS);
            addSchemaValue(schema, `${section}.titleInsideFormGroup`, true);
            addSchemaValue(schema, `${section}.titleProps`, SECTION_TITLE_PROPS);
        }
    });
    if (get(schema, `${subscriptionPath}.type`)) {
        addSchemaValue(schema, `${subscriptionPath}.title`, '');
        addSchemaValue(schema, `${subscriptionPath}.titleInsideFormGroup`, true);
        addSchemaValue(schema, `${subscriptionPath}.boxProps`, SUBSCRIPTION_BOX_PROPS);
    }
};

/**
 * Validate subscription fields in the form.
 * Shared across Create, Details, and Edit pages.
 *
 * @param {Object} formData - The form data object
 * @param {Object} errors - The errors object from RJSF
 * @param {Object} options
 * @param {boolean} options.showSubscriptions - Whether subscriptions are visible (caller-specific guard)
 */
/**
 * Validate phone number fields in the form.
 * Shared across Create, Details, and Edit pages.
 *
 * @param {Object} formData - The form data object
 * @param {Object} errors - The errors object from RJSF
 * @return {Object} The errors object
 */
export const validatePhoneFields = (formData, errors) => {
    const phone = get(formData, 'userInfo.phone');
    const cellPhone = get(formData, 'userInfo.cellPhone');
    const directLinePhone = get(formData, 'userInfo.directLinePhone');

    if (phone && !isValidPhoneNumber(phone)) {
        errors.userInfo.phone.addError(MESSAGES.FORM.FORMAT_PHONE_NUMBER_ALLOWED);
    }
    if (cellPhone && !isValidPhoneNumber(cellPhone)) {
        errors.userInfo.cellPhone.addError(MESSAGES.FORM.FORMAT_PHONE_NUMBER_ALLOWED);
    }
    if (directLinePhone && !isValidPhoneNumber(directLinePhone)) {
        errors.userInfo.directLinePhone.addError(MESSAGES.FORM.FORMAT_PHONE_NUMBER_ALLOWED);
    }
    return errors;
};

/**
 * Validate pronouns field in the form.
 * Shared across Create, Details, and Edit pages.
 *
 * @param {Object} formData - The form data object
 * @param {Object} errors - The errors object from RJSF
 * @return {Object} The errors object
 */
export const validatePronounsField = (formData, errors) => {
    const pronouns = get(formData, 'userInfo.pronouns');
    if (!isEmpty(pronouns) && pronouns.length > USERS.MAX_NUMBER_OF_PRONOUNS_ALLOWED) {
        errors.userInfo.pronouns.addError(`* ${MESSAGES.FORM.PRONOUNS_LIMIT}`);
    }
    return errors;
};

export const validateSubscriptionFields = (formData, errors, { showSubscriptions }) => {
    const isSubscribedToLawAlerts = get(
        formData,
        'subscriptions.userSubscriptions.lawAlerts.isSubscribed',
        false
    );
    const lawAlertStates = get(formData, 'subscriptions.userSubscriptions.lawAlerts.states', []);

    if (isSubscribedToLawAlerts && showSubscriptions && isEmpty(lawAlertStates)) {
        if (isLawUpdatesSubscriptionFeatureEnabled()) {
            errors.subscriptions.userSubscriptions.addError(' ');
        } else {
            errors.subscriptions.userSubscriptions.addError(MESSAGES.FORM.SELECT_LAW_ALERTS_STATES);
        }
    }

    if (isLawUpdatesSubscriptionFeatureEnabled() && showSubscriptions) {
        const isSubscribedToLawUpdates = get(
            formData,
            'subscriptions.userSubscriptions.lawUpdates.isSubscribed',
            false
        );
        const lawUpdatesStates = get(
            formData,
            'subscriptions.userSubscriptions.lawUpdates.states',
            []
        );
        const lawUpdatesCategories = get(
            formData,
            'subscriptions.userSubscriptions.lawUpdates.categories',
            []
        );
        if (isSubscribedToLawUpdates && isEmpty(lawUpdatesStates)) {
            errors.subscriptions.userSubscriptions.addError(' ');
        }
        if (isSubscribedToLawUpdates && isEmpty(lawUpdatesCategories)) {
            errors.subscriptions.userSubscriptions.addError(' ');
        }
    }
};

/**
 * Returns the DOM id of the first invalid subscription field, or null if all valid.
 * Mirrors the exact conditions used by `validateSubscriptionFields`.
 * `id` is the idSchema-derived base id used by UserSubscriptionsLayout.
 * @param {Object} formData - The form data object containing subscription information.
 * @param {Object} options - Configuration options.
 * @param {boolean} options.showSubscriptions - Whether subscriptions are shown.
 * @param {string} options.id - The idSchema-derived base id used by UserSubscriptionsLayout.
 * @return {string|null} The DOM id of the first invalid field, or null if all valid.
 */
export const getFirstInvalidSubscriptionFieldId = (formData, { showSubscriptions, id }) => {
    if (!showSubscriptions) return null;

    const isLawAlerts = get(
        formData,
        'subscriptions.userSubscriptions.lawAlerts.isSubscribed',
        false
    );
    const lawAlertStates = get(formData, 'subscriptions.userSubscriptions.lawAlerts.states', []);
    if (isLawAlerts && isEmpty(lawAlertStates)) {
        return `${id}__law-alerts-states`;
    }

    if (isLawUpdatesSubscriptionFeatureEnabled()) {
        const isLawUpdates = get(
            formData,
            'subscriptions.userSubscriptions.lawUpdates.isSubscribed',
            false
        );
        const lawUpdatesStates = get(
            formData,
            'subscriptions.userSubscriptions.lawUpdates.states',
            []
        );
        const lawUpdatesCategories = get(
            formData,
            'subscriptions.userSubscriptions.lawUpdates.categories',
            []
        );
        if (isLawUpdates && isEmpty(lawUpdatesStates)) {
            return `${id}__law-updates-states`;
        }
        if (isLawUpdates && isEmpty(lawUpdatesCategories)) {
            return `${id}__law-updates-categories`;
        }
    }
    return null;
};

/**
 * Scrolls to and focuses the first invalid subscription field.
 * Combines getFirstInvalidSubscriptionFieldId with scroll/focus logic.
 * Used as the onError handler for RJSF forms with subscription fields.
 *
 * @param {Object} formData - The form data object containing subscription information.
 * @param {Object} options - Configuration options.
 * @param {boolean} options.showSubscriptions - Whether subscriptions are shown.
 * @param {string} [options.fieldIdPrefix='root_subscriptions_userSubscriptions'] - The idSchema-derived base id.
 * @param {number} [options.scrollOffset=100] - Pixels to offset from top when scrolling.
 */
export const scrollToFirstInvalidSubscriptionField = (
    formData,
    {
        showSubscriptions,
        fieldIdPrefix = 'root_subscriptions_userSubscriptions',
        scrollOffset = 100,
    }
) => {
    const targetId = getFirstInvalidSubscriptionFieldId(formData, {
        showSubscriptions,
        id: fieldIdPrefix,
    });
    if (!targetId) return;

    const element = document.getElementById(targetId);
    if (!element) return;

    const top = element.getBoundingClientRect().top + window.pageYOffset - scrollOffset;
    window.scrollTo({ top, behavior: 'smooth' });

    if (typeof element.focus === 'function') {
        const previousTabIndex = element.getAttribute('tabindex');
        if (previousTabIndex === null) {
            element.setAttribute('tabindex', '-1');
        }
        element.focus({ preventScroll: true });
        if (previousTabIndex === null) {
            element.removeAttribute('tabindex');
        }
    }
};
